import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { Api } from '@/api';
import type { CalendarConnection } from '@/api/calendar_connections';
import type { TeamMemberEntity } from '@/api/entity_types/TeamMember';
import { emptyTeamEntity, type PipelineReviewStatus, type TeamEntity } from '@/api/entity_types/Team';

export type MemberAssignment = 'Phone Screen' | 'Interview' | 'E-Signatures';

export const useTeamStore = defineStore('team', () => {
  const teamSetupProgress = ref<number>(100);
  const team = ref<TeamEntity>({ ...emptyTeamEntity });
  const allTeamMembers = ref<TeamMemberEntity[]>([]);
  const isLoading = ref(false);

  const calendarConnections = ref<CalendarConnection[]>([]);
  const isLoadingCalendarConnections = ref(false);
  const calendarConnectionsLoaded = ref(false);

  const isLoanOfficerRecruiting = computed(() => team.value.recruiting_job_type === 'loan_officer');
  const activeTeamMembers = computed(() => allTeamMembers.value.filter(member => member.status === 'active'));
  const membersWithCalendar = computed(() =>
    activeTeamMembers.value.filter(member =>
      calendarConnections.value.some(c => c.id === member.id && c.calendar_connected)
    )
  );

  const setTeamSetupProgress = (progress: number) => {
    if (teamSetupProgress.value !== progress) {
      teamSetupProgress.value = progress;
    }
  };

  const reloadTeam = async () => {
    if (isLoading.value) return;

    isLoading.value = true;
    try {
      const response = await Api.Team.getTeam();
      team.value = response.record;
      allTeamMembers.value = response.team_members;
    } finally {
      isLoading.value = false;
    }
  };

  const setPipelineReviewStatus = (value: PipelineReviewStatus) => {
    team.value.pipeline_review_status = value;
  };

  const getMemberAssignments = (userId: number): MemberAssignment[] => {
    const assignments: MemberAssignment[] = [];
    if (team.value.phone_screen_user_id === userId) assignments.push('Phone Screen');
    if (team.value.interview_user_id === userId) assignments.push('Interview');
    if (team.value.esigner_user_id === userId) assignments.push('E-Signatures');
    return assignments;
  };

  const getScheduleFollowupLink = (contactId: number, utmMedium: string): string | null => {
    if (team.value.calendar_option === 'Calendly') {
      return team.value.calendly_link_followup;
    } else {
      if (!team.value.slug) return null;
      return `/interview/${team.value.slug}?event_type=interview&contact_id=${contactId}&utm_medium=${utmMedium}`;
    }
  };

  const loadCalendarConnections = async () => {
    if (team.value.calendar_option !== 'Humaniz') return;
    if (calendarConnectionsLoaded.value || isLoadingCalendarConnections.value) return;

    isLoadingCalendarConnections.value = true;
    try {
      const response = await Api.CalendarConnections.getCalendarConnections();
      if (response.status === 'success') {
        calendarConnections.value = response.data;
        calendarConnectionsLoaded.value = true;
      }
    } finally {
      isLoadingCalendarConnections.value = false;
    }
  };

  return {
    teamSetupProgress,
    setTeamSetupProgress,
    isLoanOfficerRecruiting,
    allTeamMembers,
    activeTeamMembers,
    membersWithCalendar,
    isLoadingCalendarConnections,
    calendarConnectionsLoaded,
    loadCalendarConnections,
    team,
    isLoading,
    reloadTeam,
    setPipelineReviewStatus,
    getMemberAssignments,
    getScheduleFollowupLink,
  };
});
